import { useEffect, useState } from "react";
import {
  FilterSortType,
  filterStateI,
} from "../pages/home/contexts/filterContext";
import { HotelDataListI } from "../redux/slices/enviromentSlice/EnviromentSlice.type";
import { getMinPrice } from "../utils/getPriceUtils";
import useQuery from "./useQuery";

const useFilter = (
  initialArray: HotelDataListI[],
  filterState: filterStateI
) => {
  const [filteredArray, setFilteredArray] = useState<
    Array<HotelDataListI | undefined>
  >([]);

  const freeCancelableFilter = (array: HotelDataListI[]) => {
    return filterState.isFreeCancelable === FilterSortType.UNSET
      ? array
      : filterState.isFreeCancelable
      ? array.filter(
          (property) =>
            !!property.priceRefundable || !!property.priceNoOfferRefundable
        )
      : array.filter(
          (property) =>
            !!property.priceNoOfferNotRefundable ||
            !!property.priceNotRefundable
        );
  };

  const orderBySort = (array: HotelDataListI[]) => {
    return array.sort((x, y) => {
      const xPriceToSort =
        filterState.isFreeCancelable === FilterSortType.UNSET
          ? getMinPrice([
              x.priceRefundable,
              x.priceNoOfferRefundable,
              x.priceNotRefundable,
              x.priceNoOfferNotRefundable,
            ])
          : filterState.isFreeCancelable
          ? getMinPrice([x.priceRefundable, x.priceNoOfferRefundable])
          : getMinPrice([x.priceNotRefundable, x.priceNoOfferNotRefundable]);
      const yPriceToSort =
        filterState.isFreeCancelable === FilterSortType.UNSET
          ? getMinPrice([
              y.priceRefundable,
              y.priceNoOfferRefundable,
              y.priceNotRefundable,
              y.priceNoOfferNotRefundable,
            ])
          : filterState.isFreeCancelable
          ? getMinPrice([y.priceRefundable, y.priceNoOfferRefundable])
          : getMinPrice([y.priceNotRefundable, y.priceNoOfferNotRefundable]);
      return !!xPriceToSort && !!yPriceToSort
        ? filterState.orderBy === FilterSortType.ASCENDING
          ? +xPriceToSort - +yPriceToSort
          : +yPriceToSort - +xPriceToSort
        : 0;
    });
  };

  const availabilitySort = (array: HotelDataListI[]) => {
    return array.sort((a, b) => {
      if (a.available && !b.available) return -1;
      if (!a.available && b.available) return 1;
      if (a.available && b.available) return 0;
      if (a.allowAllGuest && !b.allowAllGuest) return -1;
      if (!a.allowAllGuest && b.allowAllGuest) return 1;
      return 0;
    });
  };

  const hotelTypeFilter = (array: HotelDataListI[]) => {
    return filterState.hotelTypeIds.length === 0
      ? array
      : array.filter((property) => {
          return (
            property.hotelTypeStringList &&
            property.hotelTypeStringList.find((id) =>
              filterState.hotelTypeIds.includes(id)
            )
          );
        });
  };

  const amenitiesFilter = (array: HotelDataListI[]) => {
    const newArray: HotelDataListI[] = [];

    array.forEach((property, index) => {
      const amenitiesStringList = property.amenitiesList?.map(
        (amenity) => amenity.id
      );
      let booleanArray: boolean[] = [];

      filterState.amenitiesIds.forEach((id) =>
        amenitiesStringList?.includes(id)
          ? booleanArray.push(true)
          : booleanArray.push(false)
      );

      booleanArray.every(Boolean) && newArray.push(array[index]);
    });

    return filterState.amenitiesIds.length === 0 ? array : newArray;
  };

  const priceSplicer = (array: HotelDataListI[]) => {
    return array.filter((property) => {
      const price =
        filterState.isFreeCancelable === FilterSortType.UNSET
          ? getMinPrice([
              property.priceRefundable,
              property.priceNoOfferRefundable,
              property.priceNotRefundable,
              property.priceNoOfferNotRefundable,
            ])
          : filterState.isFreeCancelable
          ? getMinPrice([
              property.priceRefundable,
              property.priceNoOfferRefundable,
            ])
          : getMinPrice([
              property.priceNotRefundable,
              property.priceNoOfferNotRefundable,
            ]);
      return filterState.priceRange?.length
        ? price &&
            +price >= +filterState.priceRange[0] &&
            +price <= +filterState.priceRange[1]
        : array;
    });
  };

  const orderById = (array: HotelDataListI[], id: string) => {
    return id
      ? [
          array.find((property) => property.id === id),
          ...array.filter((property) => property.id !== id),
        ]
      : array;
  };

  const pipeline = (array: HotelDataListI[]) => {
    return freeCancelableFilter(
      priceSplicer(
        hotelTypeFilter(amenitiesFilter(availabilitySort(orderBySort(array))))
      )
    );
  };

  const queryParams = useQuery();

  useEffect(() => {
    setFilteredArray(
      orderById(pipeline(initialArray), queryParams.get("propertyId") || "")
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterState]);

  return { filteredArray };
};

export default useFilter;
